import { format, parseISO } from 'date-fns'
import React, { FC, useEffect, useState } from 'react'

import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material'
import { useParams } from 'react-router'
import { isEqual } from 'lodash'

import Accordion from '../../../accordion/common/Accordion'
import ViewsLoader from '../../../loader/ViewsLoader'
import { DaysTabs } from '../../common/DaysTabs/DaysTabs'
import ExhibitorOrderOfGoAccordion from './accordions/ExhibitorOrderOfGoAccordion'

import FirestoreService from '../../../../services/firestoreService'
import RegistrationDyDayService from '../../../../services/firebase-service/registration-by-day/RegistrationDyDayService'

import useHorse from '../../../../pages/organizer/manage/common/table/HorsesManage/hooks/useHorse'
import { getConvertedData } from '../../../../models/interface.helper'
import { sortByOrder } from '../../../../pages/organizer/manage/ManageClinicNOtherOperationsTab/data/operations-tab-utils'
import {
  getAllRegistrationFees,
  getGroupFeesByDayAndFacility,
} from '../../../../helpers/registrationFees'

import { EventFeesModel } from '../../../../models/event-fees/event-fees.model'
import { EventModel } from '../../../../models/events/event.model'
import { EventReviewPublishModel } from '../../../../models/event-review-publish/event-review-publish.model'

import { IEventReviewPublish } from '../../../../models/event-review-publish/event-review-publish.interface'
import { RegistrationFeesType } from '../../../../models/event-fees/event-fees.interface'
import { IBreak } from '../../../../models/events/event.interface'
import { IRegistrationByDayInterface } from '../../../../models/registrations-by-day/registrationByDay.interface'

import { CONST } from '../../../../const/const'
import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../../../types/payment'

const formatTimeTo12Hour = (time: string | undefined) => {
  if (!time) return 'N/A'
  const [hour, minute] = time.split(':').map(Number)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const formattedHour = hour % 12 || 12
  return `${formattedHour}:${minute.toString().padStart(2, '0')} ${ampm}`
}

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface ScheduleByRiderProps {
  activeDay: string
  setActiveDay: (value: string) => void
  days: string[]
  setDays: (value: string[]) => void
  hideEntries: boolean
}
export const ScheduleByRider: FC<ScheduleByRiderProps> = ({
  activeDay,
  setActiveDay,
  days,
  setDays,
  hideEntries,
}) => {
  const { eventId } = useParams<{ eventId: string }>()
  const { horses } = useHorse(eventId)

  const [loading, setLoading] = useState<boolean>(true)
  const [classes, setClasses] = useState<any | null>(null)
  const [publishedEvent, setPublishedEvent] = useState<IEventReviewPublish | null>(null)

  const handleGetRiderData = async () => {
    setLoading(true)
    try {
      let riders_ = await RegistrationDyDayService.getByEventIds([eventId])
      riders_ = riders_.filter((rider) => !rider.orderOfGoScratched)

      const riders = riders_.reduce((acc: IRegistrationByDayInterface[], currentItem) => {
        const isRiderIdExist = acc.some(
          (item) =>
            item.uuid === currentItem.uuid &&
            item.riderId === currentItem.riderId &&
            isEqual(item.horsesSelected, currentItem.horsesSelected)
        )

        if (!isRiderIdExist) {
          acc.push(currentItem)
        }
        return acc
      }, [])

      const eventSnapShot = await FirestoreService.getItem(COLLECTIONS.EVENTS.NAME, eventId)
      const eventData = getConvertedData(EventModel.fromFirestoreDoc(eventSnapShot).toObject())

      const eventBreaks: IBreak[] = eventData?.breaks || []

      // Fetch registration fees
      const snapShot = await FirestoreService.getItem(COLLECTIONS.EVENT_FEES.NAME, eventId)
      const registrationFees = new EventFeesModel(snapShot.data()).toFirestore()

      const eventReviewPublishSnap = await FirestoreService.getItem(
        COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
        eventId
      )

      const flattenedFees = getAllRegistrationFees(registrationFees?.registrationFees || [])

      const publishedEvent_ =
        EventReviewPublishModel.fromFirestoreDoc(eventReviewPublishSnap).toObject()
      setPublishedEvent(publishedEvent_)

      const groupFeesByDayAndFacility = getGroupFeesByDayAndFacility(flattenedFees)

      //  Group registrations by day + facility
      const groupedByDayFacility: Record<string, any[]> = {}
      const uniqueDays = new Set<string>() // To store unique days for DaysTabs

      Object.entries(groupFeesByDayAndFacility).forEach(([groupKey, { fees }]) => {
        const feeUuids = fees.map(({ uuid }) => uuid)
        const [dayKey] = groupKey.split('_') // Extract the day

        //  Get all registrations that belong to any class on this day
        let registrationsForGroup = riders.filter(
          (registration: IRegistrationByDayInterface) =>
            registration.uuid &&
            feeUuids.includes(registration.uuid) &&
            (registration.paymentType !== PAYMENT_TYPE.DEFAULT ||
              registration.paymentStatus !== PAYMENT_STATUS.DEFAULT)
        )

        let count = 0

        //  Enrich each registration
        registrationsForGroup = registrationsForGroup.map((rider: IRegistrationByDayInterface) => {
          // Find the fee whose UUID matches the registrations UUID
          const matchingFee: RegistrationFeesType | undefined = flattenedFees?.find(
            (feeItem: RegistrationFeesType) => feeItem.uuid === rider.uuid
          )

          if (!rider.orderOfGoScratched && rider.order !== 0) count++

          return {
            ...rider,
            order:
              rider.orderOfGoScratched || rider.order === 0
                ? // || rider.isScratched
                  0
                : rider.order || count + 1,
            fee: matchingFee,
          }
        })

        // Fetch breaks for this group
        const breaksForGroup = eventBreaks.filter((brk) => brk.groupKey === groupKey)

        // Combine breaks and registrations, then sort
        groupedByDayFacility[groupKey] = sortByOrder([...registrationsForGroup, ...breaksForGroup])
        uniqueDays.add(dayKey) // Add unique days for filtering
      })

      setClasses(groupedByDayFacility)

      const days_ = [...Array.from(uniqueDays)]
      setDays(days_)
      setActiveDay(days_[0])
    } catch (error) {
      console.error(error, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    handleGetRiderData().then()
  }, [])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[100px]">
        <ViewsLoader
          className="flex items-center w-full justify-center min-h-[30vh] mt-3"
          size="lg"
          color="#F70763"
        />
      </div>
    )
  }

  return Object.entries(classes || []).length > 0 ? (
    <>
      <DaysTabs activeDay={activeDay} setActiveDay={setActiveDay} days={days} isRiderMode={true} />

      {Object.entries(classes || [])
        .filter(([groupKey]) => groupKey.startsWith(activeDay))
        .map(([groupKey, riderEntries]) => {
          const typedRiderEntries = riderEntries as any
          const [dayKey, facility] = groupKey.split('_')

          // Filter actual registrations (excluding breaks)
          const registrations = typedRiderEntries.filter(
            (entry: any) => !('isBreakDrag' in entry && entry.isBreakDrag)
          )

          // Get the earliest ride time from valid registrations

          const earliestRideTime = registrations.length
            ? formatTimeTo12Hour(
                registrations
                  .map((entry: any) => entry.rideTime)
                  .filter(
                    (time: any): time is string => typeof time === 'string' && time.trim() !== ''
                  )
                  .sort()[0]
              )
            : 'N/A'

          const isPublishOrder =
            publishedEvent?.EventSchedule?.riderGroupPublish?.[groupKey]?.publishOrder || false

          return (
            <Accordion
              key={groupKey}
              disabled={hideEntries}
              icon={<AddCircleOutline className="add_icon text-[red]" />}
              initialState={false}
              status={isPublishOrder ? 'Available' : 'Pending'}
              className="mb-2 w-full"
              header={
                <div className="flex flex-col xl:flex-row gap-2 text-[14px] justify-between pr-[10px]">
                  <div className="flex gap-2 items-center">
                    <span className="text-[14px] font-semibold">
                      {`${format(parseISO(dayKey), 'EEEE')} rides`}
                    </span>
                  </div>
                  <div className="text-[#122B4680] font-normal flex items-center gap-1">
                    <span className="customValue text-[#122B46]">{registrations.length}</span>
                    rides •
                    {isPublishOrder && (
                      <>
                        <span className="customValue text-[#122B46]">
                          {earliestRideTime ? earliestRideTime : 'N/A'}
                        </span>{' '}
                      </>
                    )}
                    {format(parseISO(dayKey), 'EEEE')} •
                    <span className="customValue text-[#122B46]">{facility}</span>
                  </div>
                </div>
              }
              headerTextClassName="text-SeabiscuitDark200ThemeColor capitalize w-full pr-2"
              activeIcon={<RemoveCircleOutline className="add_icon text-[red]" />}
            >
              <ExhibitorOrderOfGoAccordion
                data={typedRiderEntries}
                isPublishOrder={isPublishOrder}
                horses={horses}
                eventId={eventId}
                isRiderMode={true}
                flattenedFees={getAllRegistrationFees(
                  publishedEvent?.EventFees?.registrationFees || []
                )}
              />
            </Accordion>
          )
        })}
    </>
  ) : (
    <p className="text-SeabiscuitDark200ThemeColor/50">No rider schedules available</p>
  )
}
